// Static translations dictionary.
// Values may contain inline HTML (<strong>, <code>) — they are trusted
// because they are hardcoded here, not user-supplied. No XSS risk.

export const translations = {
  en: {
    'hero.eyebrow': 'Ableton · Max for Live · Open source',
    'hero.logo.alt': 'agent4live logo',
    'hero.h1': 'Pilot Ableton Live with your AI agent',
    'hero.tagline':
      "Learn by talking, automate what slows you down. It backs you up — you're the one creating.",
    'hero.cta': 'Download agent4live',
    'cta.platform.current': 'macOS',
    'cta.platform.soon': 'Windows · coming soon',

    'usage.heading': 'How to use it',
    'usage.step1.title': 'Drop the device',
    'usage.step1.body': 'Launch Ableton Live and drag the <code>.amxd</code> file onto any track.',
    'usage.step2.title': 'Launch your agent',
    'usage.step2.body': 'Open your terminal and start Claude Code, Gemini or OpenCode.',
    'usage.step3.title': 'Give your instructions',
    'usage.step3.intro': 'For example:',
    'usage.step3.example1': "What's the tempo and how many tracks do I have?",
    'usage.step3.example2': 'Add a four-on-the-floor on the kick at 124.',

    'how.heading': 'Setup, done once',
    'how.lead':
      "The full sequence happens only on first launch. Next time, just drop the device on any track and everything's active instantly.",
    'how.step1.title': 'Drop the device',
    'how.step1.body': 'Launch Ableton Live and drag the <code>.amxd</code> file onto any track.',
    'how.step2.title': 'Give your consent',
    'how.step2.body': 'The device installs its helper into your Ableton library.',
    'how.step3.title': 'Restart Live',
    'how.step3.body': 'Once, so Ableton picks up the helper.',
    'how.step4.title': 'Activate the script',
    'how.step4.body': 'Preferences → Tempo & MIDI → Control Surfaces → agent4live.',
    'how.step5.title': 'Pick your agent',
    'how.step5.body': 'Claude Code, Gemini or OpenCode — tick the box.',
    'how.step6.title': "You're ready",
    'how.step6.body': 'The device shows <code>MCP ok</code>. You can now talk to your agent.',

    'tools.heading': 'Everything the agent pilots',
    'tools.exclude_label': 'Not covered:',
    'tools.transport.body':
      'Play / stop / continue, tempo, metronome, recording, tap, undo / redo.',
    'tools.session.body':
      'Tempo, time signature, scale (Live 12+), grooves, selection, swing, automation.',
    'tools.tracks.body':
      'Volume, pan, sends, mute / solo / arm, routing, devices, master, returns, crossfader, take lanes.',
    'tools.clips.body': 'Create / delete / fire, MIDI notes, warp markers, envelopes, quantize.',
    'tools.scenes.body':
      'Fire with options, create, duplicate, delete, per-scene tempo and signature.',
    'tools.arrangement.body': 'Song time, loop, punch, cue points, take lane clips.',
    'tools.racks.body': 'Audio, Instrument and Drum racks. Chains, drum pads, macros, variations.',
    'tools.instruments.body': 'Simpler, Looper, Sample mode parameters, slicing.',
    'tools.application.body':
      'Control surfaces, view navigation, Hot-Swap toggle, dialog automation.',
    'tools.midi.body':
      'Send raw MIDI messages (notes, CC) to drive external hardware or an IAC bus.',
    'tools.browser.body':
      'Load presets, instruments, effects, drum kits, samples programmatically.',
    'tools.tuning.body':
      'Tuning systems (Live 12+) — read active microtonal grid, reference pitch.',
    'tools.meta.body':
      '<code>get_usage_guide</code> — bundled skill that primes the agent with conventions and recipes.',
    'tools.raw.body':
      'Direct LOM <code>get</code> / <code>set</code> / <code>call</code> for anything not covered semantically.',
    'tools.stream.body':
      'SSE streaming — subscribe to LiveAPI observers and receive push notifications when tempo, track props, or clip positions change in Live.',

    'limits.item1': 'Freeze / Unfreeze a track',
    'limits.item2': 'Sub-10 ms scheduling for live performance',
    'limits.item3': 'Rename the master track',
    'limits.item4': 'Track monitoring state (In / Auto / Off)',
    'limits.item5': 'Delete a take lane',

    'header.github.aria': 'agent4live on GitHub',
    'header.x.aria': 'Romain Sauvez on X',
    'header.lang.aria': 'Change language',
    'header.lang.menu.aria': 'Select language',

    'consent.title': 'Cookies & analytics',
    'consent.body': 'agent4live uses Firebase Analytics to understand how the site is used.',
    'consent.accept': 'Accept',
    'consent.reject': 'Refuse',
    'consent.revoke': 'Cookie preferences',
  },

  fr: {
    'hero.eyebrow': 'Ableton · Max for Live · Open source',
    'hero.logo.alt': 'logo agent4live',
    'hero.h1': 'Pilote Ableton Live avec ton agent IA',
    'hero.tagline':
      "Apprends en discutant, automatise ce qui te ralentit. Il t'épaule, c'est toi qui crées.",
    'hero.cta': 'Télécharger agent4live',
    'cta.platform.current': 'macOS',
    'cta.platform.soon': 'Windows · bientôt',

    'usage.heading': "Comment l'utiliser",
    'usage.step1.title': 'Dépose le device',
    'usage.step1.body':
      "Lance Ableton Live et glisse le fichier <code>.amxd</code> sur n'importe quelle piste.",
    'usage.step2.title': 'Lance ton agent',
    'usage.step2.body': 'Ouvre ton terminal et lance Claude Code, Gemini ou OpenCode.',
    'usage.step3.title': 'Donne tes instructions',
    'usage.step3.intro': 'Par exemple :',
    'usage.step3.example1': 'Quel est le tempo et combien de pistes ai-je ?',
    'usage.step3.example2': 'Ajoute un four-on-the-floor sur la kick à 124.',

    'how.heading': 'Le setup, une fois pour toutes',
    'how.lead':
      "Cette séquence complète ne se fait qu'au tout premier lancement. Les fois suivantes, il suffit de déposer le device pour que tout soit immédiatement actif.",
    'how.step1.title': 'Dépose le device',
    'how.step1.body':
      "Lance Ableton Live et glisse le fichier <code>.amxd</code> sur n'importe quelle piste.",
    'how.step2.title': 'Donne ton accord',
    'how.step2.body': 'Le device installe son helper dans ta librairie Ableton.',
    'how.step3.title': 'Redémarre Live',
    'how.step3.body': "Une seule fois, pour qu'Ableton prenne le helper en compte.",
    'how.step4.title': 'Active le script',
    'how.step4.body': 'Préférences → Tempo & MIDI → Control Surfaces → agent4live.',
    'how.step5.title': 'Choisis ton agent',
    'how.step5.body': 'Claude Code, Gemini ou OpenCode — coche-le.',
    'how.step6.title': "C'est prêt",
    'how.step6.body': 'Le device affiche <code>MCP ok</code>. Tu peux dialoguer avec ton agent.',

    'tools.heading': "Tout ce que l'agent pilote",
    'tools.exclude_label': 'Pas couvert :',
    'tools.transport.body':
      'Play / stop / continue, tempo, métronome, enregistrement, tap, undo / redo.',
    'tools.session.body':
      'Tempo, signature, scale (Live 12+), grooves, sélection, swing, automation.',
    'tools.tracks.body':
      'Volume, pan, sends, mute / solo / arm, routing, devices, master, returns, crossfader, take lanes.',
    'tools.clips.body':
      'Création / suppression / fire, notes MIDI, warp markers, enveloppes, quantize.',
    'tools.scenes.body':
      'Fire avec options, création, duplication, suppression, tempo et signature par scène.',
    'tools.arrangement.body': 'Song time, loop, punch, cue points, clips de take lanes.',
    'tools.racks.body': 'Racks Audio, Instrument et Drum. Chains, drum pads, macros, variations.',
    'tools.instruments.body': 'Simpler, Looper, paramètres Sample, slicing.',
    'tools.application.body':
      'Surfaces de contrôle, navigation des views, toggle Hot-Swap, automation des dialogues.',
    'tools.midi.body':
      'Envoie des messages MIDI bruts (notes, CC) pour piloter du hardware externe ou un bus IAC.',
    'tools.browser.body':
      'Charge presets, instruments, effets, kits de drum, samples par programmation.',
    'tools.tuning.body':
      "Systèmes d'accord (Live 12+) — lecture de la grille microtonale active, reference pitch.",
    'tools.meta.body':
      "<code>get_usage_guide</code> — skill embarqué qui amorce l'agent avec conventions et recettes.",
    'tools.raw.body':
      "LOM directe <code>get</code> / <code>set</code> / <code>call</code> pour ce qui n'est pas couvert sémantiquement.",
    'tools.stream.body':
      "Streaming SSE — abonne-toi aux observers LiveAPI et reçois des notifications push dès qu'un tempo, une propriété de piste ou une position de clip change dans Live.",

    'limits.item1': "Freeze / Unfreeze d'une piste",
    'limits.item2': 'Scheduling sub-10 ms pour du live',
    'limits.item3': 'Renommer la piste master',
    'limits.item4': "État de monitoring d'une piste (In / Auto / Off)",
    'limits.item5': "Suppression d'une take lane",

    'header.github.aria': 'agent4live sur GitHub',
    'header.x.aria': 'Romain Sauvez sur X',
    'header.lang.aria': 'Changer de langue',
    'header.lang.menu.aria': 'Sélectionner la langue',

    'consent.title': 'Cookies & analytics',
    'consent.body':
      'agent4live utilise Firebase Analytics pour comprendre comment le site est utilisé.',
    'consent.accept': 'Accepter',
    'consent.reject': 'Refuser',
    'consent.revoke': 'Préférences cookies',
  },

  de: {
    'hero.eyebrow': 'Ableton · Max for Live · Open Source',
    'hero.logo.alt': 'agent4live Logo',
    'hero.h1': 'Steuere Ableton Live mit deinem KI-Agenten',
    'hero.tagline':
      'Lerne im Gespräch, automatisiere, was dich aufhält. Er unterstützt dich — du erschaffst.',
    'hero.cta': 'agent4live herunterladen',
    'cta.platform.current': 'macOS',
    'cta.platform.soon': 'Windows · in Kürze',

    'usage.heading': 'So nutzt du es',
    'usage.step1.title': 'Device ablegen',
    'usage.step1.body':
      'Starte Ableton Live und ziehe die <code>.amxd</code>-Datei auf eine beliebige Spur.',
    'usage.step2.title': 'Starte deinen Agenten',
    'usage.step2.body': 'Öffne dein Terminal und starte Claude Code, Gemini oder OpenCode.',
    'usage.step3.title': 'Gib deine Anweisungen',
    'usage.step3.intro': 'Zum Beispiel:',
    'usage.step3.example1': 'Wie ist das Tempo und wie viele Spuren habe ich?',
    'usage.step3.example2': 'Füge ein Four-on-the-Floor auf der Kick mit 124 hinzu.',

    'how.heading': 'Das Setup, ein für alle Mal',
    'how.lead':
      'Die vollständige Sequenz läuft nur beim allerersten Start. Beim nächsten Mal legst du das Device einfach auf eine Spur und alles ist sofort aktiv.',
    'how.step1.title': 'Device ablegen',
    'how.step1.body':
      'Starte Ableton Live und ziehe die <code>.amxd</code>-Datei auf eine beliebige Spur.',
    'how.step2.title': 'Erteile deine Zustimmung',
    'how.step2.body': 'Das Device legt seinen Helper in deine Ableton-Library.',
    'how.step3.title': 'Live neu starten',
    'how.step3.body': 'Einmal, damit Ableton den Helper erkennt.',
    'how.step4.title': 'Aktiviere das Script',
    'how.step4.body': 'Einstellungen → Tempo & MIDI → Control Surfaces → agent4live.',
    'how.step5.title': 'Agent wählen',
    'how.step5.body': 'Claude Code, Gemini oder OpenCode — Häkchen setzen.',
    'how.step6.title': 'Fertig',
    'how.step6.body':
      'Das Device zeigt <code>MCP ok</code>. Du kannst jetzt mit deinem Agenten sprechen.',

    'tools.heading': 'Alles, was der Agent steuert',
    'tools.exclude_label': 'Nicht abgedeckt:',
    'tools.transport.body': 'Play / Stop / Continue, Tempo, Metronom, Aufnahme, Tap, Undo / Redo.',
    'tools.session.body': 'Tempo, Taktart, Skala (Live 12+), Grooves, Auswahl, Swing, Automation.',
    'tools.tracks.body':
      'Volume, Pan, Sends, Mute / Solo / Arm, Routing, Devices, Master, Returns, Crossfader, Take Lanes.',
    'tools.clips.body':
      'Erstellen / löschen / abfeuern, MIDI-Noten, Warp-Marker, Hüllkurven, Quantize.',
    'tools.scenes.body':
      'Abfeuern mit Optionen, erstellen, duplizieren, löschen, Tempo und Taktart pro Szene.',
    'tools.arrangement.body': 'Song Time, Loop, Punch, Cue Points, Take Lane Clips.',
    'tools.racks.body':
      'Audio-, Instrument- und Drum-Racks. Chains, Drum Pads, Macros, Variationen.',
    'tools.instruments.body': 'Simpler, Looper, Sample-Modus-Parameter, Slicing.',
    'tools.application.body':
      'Control Surfaces, View-Navigation, Hot-Swap-Toggle, Dialog-Automation.',
    'tools.midi.body':
      'Sende rohe MIDI-Nachrichten (Noten, CC), um externe Hardware oder einen IAC-Bus anzusteuern.',
    'tools.browser.body': 'Lade Presets, Instrumente, Effekte, Drum Kits, Samples programmatisch.',
    'tools.tuning.body':
      'Stimmungssysteme (Live 12+) — aktives mikrotonales Raster auslesen, Referenzpitch.',
    'tools.meta.body':
      '<code>get_usage_guide</code> — eingebundener Skill, der den Agenten mit Konventionen und Rezepten versorgt.',
    'tools.raw.body':
      'Direkter LOM-<code>get</code> / <code>set</code> / <code>call</code> für alles, was nicht semantisch abgedeckt ist.',
    'tools.stream.body':
      'SSE-Streaming — abonniere LiveAPI-Observer und erhalte Push-Benachrichtigungen, sobald sich Tempo, Spur-Eigenschaften oder Clip-Positionen in Live ändern.',

    'limits.item1': 'Spur einfrieren / auftauen',
    'limits.item2': 'Sub-10-ms-Scheduling für Live-Performances',
    'limits.item3': 'Master-Spur umbenennen',
    'limits.item4': 'Spur-Monitoring-Status (In / Auto / Off)',
    'limits.item5': 'Take Lane löschen',

    'header.github.aria': 'agent4live auf GitHub',
    'header.x.aria': 'Romain Sauvez auf X',
    'header.lang.aria': 'Sprache ändern',
    'header.lang.menu.aria': 'Sprache auswählen',

    'consent.title': 'Cookies & Analytics',
    'consent.body':
      'agent4live nutzt Firebase Analytics, um zu verstehen, wie die Seite genutzt wird.',
    'consent.accept': 'Akzeptieren',
    'consent.reject': 'Ablehnen',
    'consent.revoke': 'Cookie-Einstellungen',
  },

  es: {
    'hero.eyebrow': 'Ableton · Max for Live · Código abierto',
    'hero.logo.alt': 'logo de agent4live',
    'hero.h1': 'Pilota Ableton Live con tu agente de IA',
    'hero.tagline': 'Aprende conversando, automatiza lo que te frena. Él te apoya — tú creas.',
    'hero.cta': 'Descargar agent4live',
    'cta.platform.current': 'macOS',
    'cta.platform.soon': 'Windows · próximamente',

    'usage.heading': 'Cómo usarlo',
    'usage.step1.title': 'Coloca el device',
    'usage.step1.body':
      'Lanza Ableton Live y arrastra el archivo <code>.amxd</code> a cualquier pista.',
    'usage.step2.title': 'Lanza tu agente',
    'usage.step2.body': 'Abre tu terminal e inicia Claude Code, Gemini u OpenCode.',
    'usage.step3.title': 'Da tus instrucciones',
    'usage.step3.intro': 'Por ejemplo:',
    'usage.step3.example1': '¿Cuál es el tempo y cuántas pistas tengo?',
    'usage.step3.example2': 'Añade un four-on-the-floor en el bombo a 124.',

    'how.heading': 'El setup, una sola vez',
    'how.lead':
      'Esta secuencia completa solo ocurre en el primer arranque. La próxima vez, basta con soltar el device en cualquier pista y todo se activa al instante.',
    'how.step1.title': 'Coloca el device',
    'how.step1.body':
      'Lanza Ableton Live y arrastra el archivo <code>.amxd</code> a cualquier pista.',
    'how.step2.title': 'Da tu consentimiento',
    'how.step2.body': 'El device instala su helper en tu biblioteca Ableton.',
    'how.step3.title': 'Reinicia Live',
    'how.step3.body': 'Una sola vez, para que Ableton reconozca el helper.',
    'how.step4.title': 'Activa el script',
    'how.step4.body': 'Preferencias → Tempo & MIDI → Control Surfaces → agent4live.',
    'how.step5.title': 'Elige tu agente',
    'how.step5.body': 'Claude Code, Gemini u OpenCode — márcalo.',
    'how.step6.title': 'Está listo',
    'how.step6.body': 'El device muestra <code>MCP ok</code>. Ya puedes dialogar con tu agente.',

    'tools.heading': 'Todo lo que el agente pilota',
    'tools.exclude_label': 'No cubierto:',
    'tools.transport.body':
      'Play / stop / continue, tempo, metrónomo, grabación, tap, undo / redo.',
    'tools.session.body':
      'Tempo, compás, escala (Live 12+), grooves, selección, swing, automatización.',
    'tools.tracks.body':
      'Volumen, pan, envíos, mute / solo / arm, routing, devices, master, returns, crossfader, take lanes.',
    'tools.clips.body':
      'Crear / eliminar / disparar, notas MIDI, warp markers, envolventes, cuantizar.',
    'tools.scenes.body':
      'Disparar con opciones, crear, duplicar, eliminar, tempo y compás por escena.',
    'tools.arrangement.body': 'Song time, loop, punch, cue points, clips de take lanes.',
    'tools.racks.body': 'Racks Audio, Instrument y Drum. Chains, drum pads, macros, variaciones.',
    'tools.instruments.body': 'Simpler, Looper, parámetros del modo Sample, slicing.',
    'tools.application.body':
      'Control surfaces, navegación de vistas, toggle Hot-Swap, automatización de diálogos.',
    'tools.midi.body':
      'Envía mensajes MIDI directos (notas, CC) para controlar hardware externo o un bus IAC.',
    'tools.browser.body':
      'Carga presets, instrumentos, efectos, drum kits, samples de forma programática.',
    'tools.tuning.body':
      'Sistemas de afinación (Live 12+) — lectura de la cuadrícula microtonal activa, pitch de referencia.',
    'tools.meta.body':
      '<code>get_usage_guide</code> — skill incluida que prepara al agente con convenciones y recetas.',
    'tools.raw.body':
      'LOM directa <code>get</code> / <code>set</code> / <code>call</code> para lo que no esté cubierto semánticamente.',
    'tools.stream.body':
      'Streaming SSE — suscríbete a los observers de LiveAPI y recibe notificaciones push cuando cambian tempo, propiedades de pista o posiciones de clip en Live.',

    'limits.item1': 'Congelar / descongelar una pista',
    'limits.item2': 'Scheduling sub-10 ms para directo',
    'limits.item3': 'Renombrar la pista master',
    'limits.item4': 'Estado de monitoring de pista (In / Auto / Off)',
    'limits.item5': 'Eliminar una take lane',

    'header.github.aria': 'agent4live en GitHub',
    'header.x.aria': 'Romain Sauvez en X',
    'header.lang.aria': 'Cambiar idioma',
    'header.lang.menu.aria': 'Seleccionar idioma',

    'consent.title': 'Cookies y analítica',
    'consent.body': 'agent4live usa Firebase Analytics para entender cómo se usa el sitio.',
    'consent.accept': 'Aceptar',
    'consent.reject': 'Rechazar',
    'consent.revoke': 'Preferencias de cookies',
  },

  ja: {
    'hero.eyebrow': 'Ableton · Max for Live · オープンソース',
    'hero.logo.alt': 'agent4live ロゴ',
    'hero.h1': 'AI エージェントで Ableton Live を操作',
    'hero.tagline': '対話で学び、面倒な作業を自動化。サポートはおまかせ、創るのはあなた。',
    'hero.cta': 'agent4live をダウンロード',
    'cta.platform.current': 'macOS',
    'cta.platform.soon': 'Windows · 近日対応',

    'usage.heading': '使い方',
    'usage.step1.title': 'device をドロップ',
    'usage.step1.body':
      'Ableton Live を起動し、<code>.amxd</code> ファイルを任意のトラックにドラッグ。',
    'usage.step2.title': 'エージェントを起動',
    'usage.step2.body': 'ターミナルを開き、Claude Code、Gemini、OpenCode のいずれかを起動。',
    'usage.step3.title': '指示を出す',
    'usage.step3.intro': '例えば：',
    'usage.step3.example1': 'テンポは？トラックはいくつある？',
    'usage.step3.example2': '124 BPM でキックに four-on-the-floor を追加して。',

    'how.heading': 'セットアップ、一度だけ',
    'how.lead':
      'この完全なシーケンスは初回起動時のみ。次回からは、device をトラックにドロップするだけで、すぐにすべてが有効になります。',
    'how.step1.title': 'device をドロップ',
    'how.step1.body':
      'Ableton Live を起動し、<code>.amxd</code> ファイルを任意のトラックにドラッグ。',
    'how.step2.title': '同意する',
    'how.step2.body': 'device がヘルパーを Ableton ライブラリに配置します。',
    'how.step3.title': 'Live を再起動',
    'how.step3.body': '一度だけ、Ableton にヘルパーを認識させるため。',
    'how.step4.title': 'スクリプトを有効化',
    'how.step4.body': '環境設定 → Tempo & MIDI → Control Surfaces → agent4live。',
    'how.step5.title': 'エージェントを選択',
    'how.step5.body': 'Claude Code、Gemini、OpenCode のどれかにチェック。',
    'how.step6.title': '準備完了',
    'how.step6.body': 'device に <code>MCP ok</code> と表示されたら、エージェントと話せます。',

    'tools.heading': 'エージェントが操作するすべて',
    'tools.exclude_label': '対象外：',
    'tools.transport.body':
      'Play / stop / continue、テンポ、メトロノーム、録音、tap、undo / redo。',
    'tools.session.body':
      'テンポ、拍子、スケール（Live 12+）、グルーヴ、選択、スウィング、オートメーション。',
    'tools.tracks.body':
      'ボリューム、パン、センド、mute / solo / arm、ルーティング、device、master、return、クロスフェーダー、テイクレーン。',
    'tools.clips.body':
      '作成 / 削除 / fire、MIDI ノート、ワープマーカー、エンベロープ、クオンタイズ。',
    'tools.scenes.body': 'オプション付き fire、作成、複製、削除、シーンごとのテンポと拍子。',
    'tools.arrangement.body':
      'ソングタイム、ループ、パンチ、キューポイント、テイクレーンのクリップ。',
    'tools.racks.body':
      'Audio、Instrument、Drum ラック。チェーン、ドラムパッド、マクロ、バリエーション。',
    'tools.instruments.body': 'Simpler、Looper、Sample モードパラメータ、スライシング。',
    'tools.application.body':
      'コントロールサーフェス、ビューナビゲーション、Hot-Swap トグル、ダイアログのオートメーション。',
    'tools.midi.body':
      '生 MIDI メッセージ（ノート、CC）を送信して外部ハードウェアや IAC バスを駆動。',
    'tools.browser.body':
      'プリセット、インストゥルメント、エフェクト、ドラムキット、サンプルをプログラム的に読み込み。',
    'tools.tuning.body':
      'チューニングシステム（Live 12+） — アクティブなマイクロトーナルグリッドとリファレンスピッチを取得。',
    'tools.meta.body':
      '<code>get_usage_guide</code> — エージェントに規約とレシピを伝える組み込みスキル。',
    'tools.raw.body':
      'セマンティックに対応していないものへの直接 LOM <code>get</code> / <code>set</code> / <code>call</code>。',
    'tools.stream.body':
      'SSE ストリーミング — LiveAPI オブザーバーを購読し、Live 内のテンポ・トラックプロパティ・クリップ位置の変更を即座にプッシュで受信。',

    'limits.item1': 'トラックの Freeze / Unfreeze',
    'limits.item2': 'ライブパフォーマンス向けの 10 ms 未満スケジューリング',
    'limits.item3': 'master トラックの名称変更',
    'limits.item4': 'トラックのモニタリング状態（In / Auto / Off）',
    'limits.item5': 'テイクレーンの削除',

    'header.github.aria': 'GitHub の agent4live',
    'header.x.aria': 'X の Romain Sauvez',
    'header.lang.aria': '言語を変更',
    'header.lang.menu.aria': '言語を選択',

    'consent.title': 'クッキーと分析',
    'consent.body': 'agent4live は Firebase Analytics でサイトの使われ方を把握しています。',
    'consent.accept': '承認',
    'consent.reject': '拒否',
    'consent.revoke': 'クッキー設定',
  },

  zh: {
    'hero.eyebrow': 'Ableton · Max for Live · 开源',
    'hero.logo.alt': 'agent4live 标志',
    'hero.h1': '用 AI 智能体驾驭 Ableton Live',
    'hero.tagline': '边聊边学，让重复操作自动化。它当后盾，你来创作。',
    'hero.cta': '下载 agent4live',
    'cta.platform.current': 'macOS',
    'cta.platform.soon': 'Windows · 即将推出',

    'usage.heading': '如何使用',
    'usage.step1.title': '放置 device',
    'usage.step1.body': '启动 Ableton Live，把 <code>.amxd</code> 文件拖到任意轨道。',
    'usage.step2.title': '启动你的智能体',
    'usage.step2.body': '打开终端，启动 Claude Code、Gemini 或 OpenCode。',
    'usage.step3.title': '给出指令',
    'usage.step3.intro': '例如：',
    'usage.step3.example1': '现在的 tempo 是多少？我有几个轨道？',
    'usage.step3.example2': '在 124 BPM 给底鼓加一个 four-on-the-floor。',

    'how.heading': '配置，一次到位',
    'how.lead':
      '这套完整流程只在首次启动时出现。之后，只需把 device 放到任意轨道，所有功能立即生效。',
    'how.step1.title': '放置 device',
    'how.step1.body': '启动 Ableton Live，把 <code>.amxd</code> 文件拖到任意轨道。',
    'how.step2.title': '同意安装',
    'how.step2.body': 'device 会把 helper 放到你的 Ableton 资源库。',
    'how.step3.title': '重启 Live',
    'how.step3.body': '只需一次，让 Ableton 识别 helper。',
    'how.step4.title': '激活脚本',
    'how.step4.body': '偏好设置 → Tempo & MIDI → Control Surfaces → agent4live。',
    'how.step5.title': '选择智能体',
    'how.step5.body': '勾选 Claude Code、Gemini 或 OpenCode。',
    'how.step6.title': '准备就绪',
    'how.step6.body': 'device 显示 <code>MCP ok</code>，你可以和智能体对话了。',

    'tools.heading': '智能体能控制的一切',
    'tools.exclude_label': '未覆盖：',
    'tools.transport.body': 'Play / stop / continue、tempo、节拍器、录制、tap、undo / redo。',
    'tools.session.body': 'Tempo、拍号、音阶（Live 12+）、groove、选择、swing、自动化。',
    'tools.tracks.body':
      '音量、声像、send、mute / solo / arm、路由、device、master、return、交叉推子、take lane。',
    'tools.clips.body': '创建 / 删除 / fire、MIDI 音符、warp 标记、包络、量化。',
    'tools.scenes.body': '带选项 fire、创建、复制、删除、按场景的 tempo 与拍号。',
    'tools.arrangement.body': '歌曲时间、loop、punch、cue 点、take lane 片段。',
    'tools.racks.body': 'Audio、Instrument 和 Drum 机架。链、鼓垫、宏、变体。',
    'tools.instruments.body': 'Simpler、Looper、Sample 模式参数、切片。',
    'tools.application.body': '控制表面、视图导航、Hot-Swap 切换、对话自动化。',
    'tools.midi.body': '发送原始 MIDI 消息（音符、CC）以驱动外部硬件或 IAC 总线。',
    'tools.browser.body': '以编程方式加载预设、乐器、效果、鼓组、采样。',
    'tools.tuning.body': '调律系统（Live 12+） — 读取当前微分音网格、参考音高。',
    'tools.meta.body': '<code>get_usage_guide</code> — 内置技能，为智能体注入约定和食谱。',
    'tools.raw.body':
      '对语义未覆盖的内容直接调用 LOM <code>get</code> / <code>set</code> / <code>call</code>。',
    'tools.stream.body':
      'SSE 流式推送 — 订阅 LiveAPI observer，当 Live 中的 tempo、轨道属性或 clip 位置变化时即时推送通知。',

    'limits.item1': '冻结 / 解冻轨道',
    'limits.item2': '适用于现场演出的低于 10 毫秒的调度',
    'limits.item3': '重命名 master 轨道',
    'limits.item4': '轨道监听状态（In / Auto / Off）',
    'limits.item5': '删除 take lane',

    'header.github.aria': 'agent4live 在 GitHub',
    'header.x.aria': 'Romain Sauvez 在 X',
    'header.lang.aria': '切换语言',
    'header.lang.menu.aria': '选择语言',

    'consent.title': 'Cookie 与分析',
    'consent.body': 'agent4live 使用 Firebase Analytics 了解网站的使用情况。',
    'consent.accept': '接受',
    'consent.reject': '拒绝',
    'consent.revoke': 'Cookie 偏好',
  },
};

export const SUPPORTED_LANGS = Object.keys(translations);
